/**
 * 文件 or 文件夹分享
 */

import CryptoJS from 'crypto-js';
import { tips, serialize } from '@/scripts/tools/utils';
import { store } from '@/scripts/store';
import dao from '@/scripts/helper/dao';

const { lang } = window;

const CONSTANT = {
  IV: 'cosbrowser-share',
};
const iv = CryptoJS.enc.Utf8.parse(CONSTANT.IV); // 密钥偏移量

const tools = {
  // 加密方法
  AESEncrypt(word, key) {
    const srcs = CryptoJS.enc.Utf8.parse(word);
    const accessCode = CryptoJS.enc.Utf8.parse(key); // 密钥
    const encrypted = CryptoJS.AES.encrypt(srcs, accessCode, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const hexStr = encrypted.ciphertext.toString().toUpperCase();
    const oldHexStr = CryptoJS.enc.Hex.parse(hexStr);
    // 将密文转为Base64的字符串
    const base64Str = CryptoJS.enc.Base64.stringify(oldHexStr);
    return base64Str;
  },
  encodeToBase64(arr) {
    const rawStr = JSON.stringify(arr);
    const wordArray = CryptoJS.enc.Utf8.parse(rawStr);
    const base64 = CryptoJS.enc.Base64.stringify(wordArray);
    return base64;
  },
  decodeOfBase64(base64) {
    const decode = CryptoJS.enc.Base64.parse(base64);
    const str = CryptoJS.enc.Utf8.stringify(decode);
    const arr = JSON.parse(str);
    return arr;
  },
  /**
   * 生成 policy 策略
   * @param {*} Bucket
   * @param {*} Region
   * @param {*} list
   * @param {*} type
   */
  generatePolicy({
    Bucket, Region, files, permission = 'r',
  }) {
    const AppId = Bucket.substr(Bucket.lastIndexOf('-') + 1);

    const resource = files.map(item => {
      let res = `qcs::cos:${Region}:uid/${AppId}:${Bucket}/${item.key}`;
      if (item.isDir) {
        res += '*';
      }
      return res;
    });

    let allowActions = [];
    switch (permission) {
      case 'rw': {
        // 读写策略，不含删除
        allowActions = [
          // 获取文件列表及信息
          'name/cos:GetBucket',
          'name/cos:GetObject',
          'name/cos:HeadObject',
          // 简单上传
          'name/cos:PostObject',
          'name/cos:PutObject',
          'name/cos:PutObjectTagging',
          // 分片上传
          'name/cos:InitiateMultipartUpload',
          'name/cos:ListMultipartUploads',
          'name/cos:ListParts',
          'name/cos:UploadPart',
          'name/cos:CompleteMultipartUpload',
          'name/cos:UploadPartCopy',
          'name/cos:AbortMultipartUpload',
          // 删除对象
          'name/cos:DeleteObject',
          'name/cos:DeleteMultipleObject',
          'name/cos:GetBucketReplication', // 删除对象时为了安全起见需检查存储桶是否开启了跨地域复制功能
        ];
        break;
      }
      default: {
        // 默认只读
        allowActions = ['name/cos:GetBucket', 'name/cos:GetObject', 'name/cos:HeadObject'];
        break;
      }
    }

    const policy = {
      version: '2.0',
      statement: [
        {
          action: allowActions,
          effect: 'allow',
          resource,
        },
      ],
    };
    const action = allowActions.map(act => act.split(':')[1]);
    return { Policy: JSON.stringify(policy), action };
  },
};

/**
 * 保存分享
 * @param {Object} opt
 * - {String} sid
 * - {String} expire 临时证书的有效期
 * - {String} token
 * - {String} Bucket
 * - {String} Region
 * - {String} Prefix
 * - {Array} files
 * - {Array} action
 * @param {String} extract
 */
function saveShareInfo(opt, extract) {
  const {
    TmpSecretId: sid, ExpiredTime: expire, Token: token, Bucket, Region, Prefix, files: list, action,
  } = opt;

  const files = list.map(item => {
    if (item.isDir) {
      return { name: encodeURIComponent(item.name) };
    }
    return { name: encodeURIComponent(item.name), size: item.size, mtime: item.mtime };
  });

  const shareInfo = serialize({
    sid,
    expire,
    token,
    Bucket,
    Region,
    Prefix,
    files: tools.encodeToBase64(files),
    action: tools.encodeToBase64(action),
  });
  return new Promise(resolve => {
    dao
      .saveSharedFilesList({
        shareInfo,
        extract,
      })
      .then(resolve)
      .catch(() => {
        tips.error(lang.t('state.fail.get_share_link'));
        resolve();
      });
  });
}

const shareApi = {
  shareLink: '',
  /**
   * 获取分享链接
   * @param {String} Bucket
   * @param {String} Region
   * @param {String} Prefix
   * @param {Number} DurationSeconds 临时证书的有效期
   * @param {Array} files 要分享的文件列表
   * @param {String} extract 提取码
   * @param {String} type 分享类型 r:只读 rw:读写
   */
  getShareLink({
    Bucket, Region, Prefix, DurationSeconds = 1800, files, extract, permission = 'r',
  }) {
    return new Promise((resolve, reject) => {
      // 1. 先配置存储桶跨域 TODO: 当支持读写操作时才需要
      // if (permission === 'rw') {
      //   const bucketCorRes = await tools.putBucketCors(Bucket, Region);
      //   if (!bucketCorRes) return;
      // }
      const AppId = Bucket.substr(Bucket.lastIndexOf('-') + 1);

      const { Policy, action } = tools.generatePolicy({
        Bucket,
        Region,
        files,
        permission,
      });
      // 2. 获取临时凭证
      dao
        .getSts({
          Region,
          Policy,
          DurationSeconds,
        })
        .then(async data => {
          const { ExpiredTime, Credentials } = data;
          const { TmpSecretId, TmpSecretKey, Token } = Credentials;

          // 3. 将要分享文件/文件夹信息保存在服务端
          const res = await saveShareInfo(
            {
              ExpiredTime,
              TmpSecretId,
              Token,
              Bucket,
              Region,
              Prefix,
              files,
              action,
            },
            extract,
          );
          if (!res) return;
          const { accessCode, shareId } = res;
          const stoken = tools.AESEncrypt(TmpSecretKey, accessCode);

          const url = `${store.cosbrowserServerUrl}/share/?id=${encodeURIComponent(shareId)}&token=${encodeURIComponent(
            stoken,
          )}`;

          console.log(
            '[share Folder success.]',
            JSON.stringify({ data: { shareId, ExpiredTime, files: files.map(v => v.key) } }),
          );

          resolve({ url, extract, expire: ExpiredTime });
        })
        .catch(err => {
          if (err) {
            let code = err.code || err;
            if (store.userInfo.AppId !== AppId) {
              code = 'curr_bucket_not_belongs_to_you';
            }
            const msg = `${lang.t('state.fail.share')} , ${lang.err(`error['${code}']`, err)}`;
            // util.error(msg);
            reject(msg);
          }
        });
    });
  },
};

export default shareApi;
